import { FC, useEffect, useMemo } from "react";
import { useFocusOnMount } from "../../../hooks/useFocusOnMount";
import { InputNumberProps } from "../types";
import { getIconFromStringOrElement } from "../../Icons/utils";
import { styles } from "../styles";
import { useInputNumber } from "./hooks/useInputNumber";

/**
 * `<Input type="number" value={number | null} />` allows users to input a number.
 */
export const InputNumber: FC<InputNumberProps> = ({
  disabled = false,
  placeholder = "",
  focusOnMount = false,
  error = false,
  value,
  prefix,
  suffix,
  min,
  max,
  step,
  precision,
  onFocus,
  onChange,
  onBlur,
  onKeyDown,
  onKeyUp,
  onClick,
  onInput,
  onPaste,
  id
}: InputNumberProps) => {
  const {
    setInputOnBlur,
    setOnInput,
    parseNumber,
    setRawValue,
    rawValue,
    inputRef
  } = useInputNumber({
    onBlur,
    onChange,
    precision,
    value,
    min,
    max
  });

  useEffect(() => {
    if (typeof value === "number") {
      setRawValue(parseNumber(value.toString())!);
    } else if (value === null) {
      setRawValue("");
    }
  }, [value]);

  const PrefixIcon = useMemo(() => {
    return getIconFromStringOrElement(prefix);
  }, [prefix]);

  const SuffixIcon = useMemo(() => {
    return getIconFromStringOrElement(suffix);
  }, [suffix]);

  useFocusOnMount(inputRef, focusOnMount);

  return (
    <div
      className="subKit-InputNumber"
      css={[
        styles.inputBase,
        !!prefix && styles.withPrefix,
        !!suffix && styles.withSuffix,
        !!disabled && styles.disabled,
        !!error && styles.withError
      ]}
    >
      {PrefixIcon && <span className="kit-input-prefix">{PrefixIcon}</span>}
      <input
        type="number"
        ref={inputRef}
        value={rawValue}
        disabled={disabled}
        id={id}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        onInput={e => {
          setOnInput(e);
          onInput?.(e);
        }}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onFocus={onFocus}
        onBlur={setInputOnBlur}
        onClick={onClick}
        onWheel={e => {
          e.currentTarget?.blur();
        }}
        onPaste={onPaste}
      />
      {SuffixIcon && <span className="kit-input-suffix">{SuffixIcon}</span>}
    </div>
  );
};
